import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Section } from '@/shared/ui/layout/Section';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { DownloadIcon } from '@/shared/ui/icons';
import { extractErrorMessage } from '@/shared/api/errors';
import { useMyReceipts } from '@/entities/receipts';
import { formatUsd } from '@/shared/utils/currency';
import { formatDate } from '@/shared/utils/date';

// Self-contained receipts (payment PDFs) section. Owns its own data hook and
// hides entirely when the customer has no receipts.
export const ReceiptsCard = () => {
  const { t } = useTranslation('profile');
  const query = useMyReceipts();

  if (query.isLoading) {
    return (
      <Section title={t('receipts.title')}>
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      </Section>
    );
  }

  if (query.isError) {
    return (
      <Section title={t('receipts.title')}>
        <p className="text-sm text-red-600">
          {query.error ? extractErrorMessage(query.error) : t('receipts.error')}
        </p>
      </Section>
    );
  }

  const receipts = query.data ?? [];

  // Hide entirely when there are no receipts.
  if (receipts.length === 0) return null;

  return (
    <Section
      title={t('receipts.title')}
      action={
        <Link
          to="/settings/payments"
          className="text-xs font-semibold text-brand-600 active:opacity-70"
        >
          {t('payments:viewAll')}
        </Link>
      }
    >
      <ul className="flex flex-col divide-y divide-slate-100">
        {receipts.map((receipt) => (
          <li key={receipt.id} className="flex items-center justify-between gap-3 py-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">
                {t('receipts.number', { number: receipt.number })}
              </p>
              <p className="truncate text-xs text-ink-muted">
                {receipt.parcel_name} · {receipt.trek_id}
              </p>
              <p className="mt-0.5 text-xs text-ink-muted">
                {formatUsd(Number(receipt.amount))} · {formatDate(receipt.created_at)}
              </p>
            </div>
            {receipt.file_url ? (
              <a
                href={receipt.file_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-ink active:opacity-70"
              >
                <DownloadIcon className="h-4 w-4" aria-hidden />
                {t('receipts.download')}
              </a>
            ) : (
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-400">
                <DownloadIcon className="h-4 w-4" aria-hidden />
                {t('receipts.download')}
              </span>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
};
